/**
 * MAP_CONFIG — checked-in seed config for the interactive studies map (plan §4.6).
 *
 * Pin LOCATIONS are config-driven by necessity: the `studies` table has no geo column, so
 * institution → lat/long lives here, not in the DB. Pin SAMPLE COUNTS come at runtime from
 * `usePublicStats().perStudySampleCounts[]` and are joined CLIENT-SIDE on `label` (the
 * institution name). Keep each `label` identical to the institution label the public-stats
 * endpoint emits (STUDY_INSTITUTION_MAP), or the count won't join.
 *
 * The email-domain → institution → lat/long mapping is a SEED CONVENIENCE for this file,
 * NOT a runtime read (plan §4.6 tech approach).
 *
 * Coordinates below are PLACEHOLDERS (TODO: replace with curated real lat/long — [INPUT]).
 * `storySlug` matches a story folder in the studysync-stories Astro repo (Task 4 manifest)
 * so a pin can deep-link to its published story where one exists.
 */

/** Which StudySync data sources flow from a given study (plan §4.2 / §4.7). */
export type DataSource = 'healthkit' | 'fitbit' | 'coremotion' | 'ehr';

export interface MapStudy {
  /** Internal study slug (matches the studysync-stories folder / story slug where present). */
  slug: string;
  /** Human-readable study title, shown (and linked) in the studies-map pin cards. */
  name: string;
  /** Institution label — MUST match the label `perStudySampleCounts[].label` uses to join counts. */
  label: string;
  /** TODO [INPUT]: real latitude. Placeholder coordinate. */
  lat: number;
  /** TODO [INPUT]: real longitude. Placeholder coordinate. */
  lng: number;
  /** Data sources collected for this study. */
  sources: DataSource[];
  /** Published-story slug for deep-linking, where a story exists. */
  storySlug?: string;
  /** Whether the study is currently active / advertisable on the public map. */
  active: boolean;
}

export const MAP_CONFIG: MapStudy[] = [
  {
    slug: 'uci-electroacupuncture-hypertension',
    name: 'Electroacupuncture for Hypertension',
    label: 'University of California, Irvine',
    lat: 33.6405, // TODO [INPUT]: placeholder — UC Irvine campus
    lng: -117.8443, // TODO [INPUT]: placeholder
    sources: ['healthkit', 'fitbit', 'ehr'],
    storySlug: 'uci-electroacupuncture-hypertension',
    active: true,
  },
  {
    slug: 'stanford-beneficial-exposome',
    name: 'Beneficial Exposome Study',
    label: 'Stanford University',
    lat: 37.4275, // TODO [INPUT]: placeholder — Stanford campus
    lng: -122.1697, // TODO [INPUT]: placeholder
    sources: ['healthkit', 'fitbit', 'coremotion', 'ehr'],
    storySlug: 'stanford-beneficial-exposome',
    active: true,
  },
  {
    slug: 'stanford-particulate-matter-cystic-fibrosis',
    name: 'Particulate Matter & Cystic Fibrosis',
    label: 'Stanford University',
    lat: 37.4275, // TODO [INPUT]: placeholder — Stanford campus
    lng: -122.1697, // TODO [INPUT]: placeholder
    sources: ['healthkit', 'fitbit', 'ehr'],
    storySlug: 'stanford-particulate-matter-cystic-fibrosis',
    active: true,
  },
  {
    slug: 'stanford-canpact-psilocybin-cancer',
    name: 'CAN-PACT: Psilocybin Therapy',
    label: 'Ontario, Canada',
    lat: 43.65, // TODO [INPUT]: placeholder — southern Ontario (Toronto area)
    lng: -79.38, // TODO [INPUT]: placeholder
    sources: ['healthkit', 'fitbit', 'ehr'],
    storySlug: 'stanford-canpact-psilocybin-cancer',
    active: true,
  },
  {
    slug: 'stanford-indonesia-heat',
    name: 'Heat Exposure in Indonesia',
    label: 'Stanford University',
    lat: 37.4275, // TODO [INPUT]: placeholder — Stanford campus (PI institution)
    lng: -122.1697, // TODO [INPUT]: placeholder
    sources: ['healthkit', 'fitbit', 'coremotion'],
    storySlug: 'stanford-indonesia-heat',
    active: true,
  },
  {
    // Field site for the Stanford Indonesia heat study — a second pin for the same study,
    // placed at the in-country research site rather than the PI's institution.
    slug: 'stanford-indonesia-heat-field-site',
    name: 'Heat Exposure in Indonesia',
    label: 'Indonesia field site',
    lat: -6.2088, // TODO [INPUT]: placeholder — Jakarta, Indonesia
    lng: 106.8456, // TODO [INPUT]: placeholder
    sources: ['healthkit', 'fitbit', 'coremotion'],
    storySlug: 'stanford-indonesia-heat',
    active: true,
  },
];

export default MAP_CONFIG;
